import Papa from 'papaparse'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  createImportPreview,
  updateImportPreview,
  useGetImportPreview,
  performSetCompanies,
  performSetTags,
  performSetDeals,
  validateDealAccountOwners,
  validateDealSalesStages,
  performSetContacts,
  performSetCompanyNotes,
  usePollStatus,
  setUnmatchedChains,
} from './importer-api'
import {
  ColumnType,
  ColumnColors,
  ColumnOption,
  CSVRow,
  ImportData,
  CSVRowState,
  ImportDataRowsFormat,
  InternalColumns,
  ImportPreviewDB,
  ImportPreviewNoData,
} from './importer-types'
import { Product } from 'models/product'
import { ChainSearchResult, DoorSearchResult } from 'features/elastic/searchers'
import { useNavigate } from 'react-router-dom'
import apiService from 'services/api'
import { toast } from 'react-toastify'
import {
  FB_REMOVE_CELL_VALUE,
  ValidateModalData,
  ValidateModalRow,
} from './validate-modal'
import { ColumnFilter, RowSelectionState } from '@tanstack/react-table'
import { ExportModalColumn } from 'components/Modals/ExportModal/ExportModal'
import { CompanyTypeSlug } from 'models/companies'
import { PATHS } from 'paths'

// === Phase 2 Helper Types and Functions ===
type CompanyInfo = {
  companyHash: string
  companyName: string
  isCustomCompany: boolean
  firstbiteId: string
  isImported: boolean
}

type HeaderLookups = {
  dealHeaders: { original: string; mapped: string }[]
  tagHeader: { original: string; mapped: string } | undefined
  contactHeaders: { original: string; mapped: string }[]
  notesHeader: { original: string; mapped: string } | undefined
  hasDealHeaders: boolean
  hasContactHeaders: boolean
}

type ImportCounts = {
  ugcs: { total: number; imported: number }
  tags: { total: number; imported: number }
  deals: { total: number; imported: number }
  contacts: { total: number; imported: number }
  notes: { total: number; imported: number }
}

function buildCompanyInfo(firstRow: CSVRow, companyHash: string): CompanyInfo {
  const isCustomCompany = firstRow[InternalColumns.IS_CUSTOM_COMPANY]
  const setChainProxyId = firstRow[InternalColumns.SET_CHAIN_PROXY_ID]
  const matchedChain = firstRow[InternalColumns.MATCHED_CHAIN]
  const companyName = isCustomCompany
    ? 'User-Generated'
    : matchedChain?.name || 'Excluded'

  let firstbiteId = ''
  if (isCustomCompany) {
    firstbiteId = 'UG' + setChainProxyId
  } else if (matchedChain) {
    firstbiteId = (matchedChain.type ?? '') + (matchedChain.item_id ?? '')
  }

  return {
    companyHash,
    companyName,
    isCustomCompany,
    firstbiteId,
    isImported: setChainProxyId !== -1,
  }
}

function processDealRow(
  row: CSVRow,
  headers: HeaderLookups,
  companyInfo: CompanyInfo,
  summaryDeals: Record<string, unknown>[],
  detailedDeals: Record<string, unknown>[],
  counts: ImportCounts,
  isSet: boolean
): void {
  if (!headers.hasDealHeaders) return

  const dealData: Record<string, unknown> = {}
  let hasData = false

  headers.dealHeaders.forEach((header) => {
    const value = row[header.original]
    if (value !== undefined && value !== null && value !== '') {
      dealData[header.mapped] = value
      hasData = true
    }
  })

  if (row[InternalColumns.DEAL_PRODUCT]) {
    dealData['deal_product'] = row[InternalColumns.DEAL_PRODUCT]
    hasData = true
  }

  if (hasData) {
    const isImported = row[InternalColumns.DEAL_SET] !== -1
    summaryDeals.push(dealData)
    detailedDeals.push({ ...companyInfo, ...dealData, isImported })
    if (isSet) {
      counts.deals.total++
      if (isImported) counts.deals.imported++
    }
  }
}

function processTagRow(
  row: CSVRow,
  headers: HeaderLookups,
  companyInfo: CompanyInfo,
  summaryTagsSet: Set<string>,
  detailedTags: Record<string, unknown>[],
  counts: ImportCounts,
  isSet: boolean
): void {
  if (!headers.tagHeader) return

  const tagValue = row[headers.tagHeader.original]
  if (!tagValue) return

  const isImported = row[InternalColumns.TAG_SET] !== -1
  const tagList = String(tagValue)
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0)

  tagList.forEach((tag) => {
    summaryTagsSet.add(tag)
    detailedTags.push({ ...companyInfo, tag_name: tag, isImported })
    if (isSet) {
      counts.tags.total++
      if (isImported) counts.tags.imported++
    }
  })
}

function processContactRow(
  row: CSVRow,
  headers: HeaderLookups,
  companyInfo: CompanyInfo,
  summaryContacts: Record<string, unknown>[],
  detailedContacts: Record<string, unknown>[],
  counts: ImportCounts,
  isSet: boolean
): void {
  if (!headers.hasContactHeaders) return

  const contactData: Record<string, unknown> = {}
  let hasData = false

  headers.contactHeaders.forEach((header) => {
    const value = row[header.original]
    if (value !== undefined && value !== null && value !== '') {
      contactData[header.mapped] = value
      hasData = true
    }
  })

  if (hasData) {
    const isImported = row[InternalColumns.CONTACT_SET] !== -1
    summaryContacts.push(contactData)
    detailedContacts.push({ ...companyInfo, ...contactData, isImported })
    if (isSet) {
      counts.contacts.total++
      if (isImported) counts.contacts.imported++
    }
  }
}

function processNoteRow(
  row: CSVRow,
  headers: HeaderLookups,
  companyInfo: CompanyInfo,
  summaryNotesSet: Set<string>,
  detailedNotes: Record<string, unknown>[],
  counts: ImportCounts,
  isSet: boolean
): void {
  if (!headers.notesHeader) return

  const noteValue = row[headers.notesHeader.original]
  if (!noteValue) return

  const isImported = row[InternalColumns.COMPANY_NOTES_SET] !== -1
  const noteList = String(noteValue)
    .split(',')
    .map((n) => n.trim())
    .filter((n) => n.length > 0)

  noteList.forEach((note) => {
    summaryNotesSet.add(note)
    detailedNotes.push({ ...companyInfo, company_note: note, isImported })
    if (isSet) {
      counts.notes.total++
      if (isImported) counts.notes.imported++
    }
  })
}
// === End Phase 2 Helper Functions ===

// === Context Setup ===

type ExportConfig = {
  allowSuggestions: boolean
  suggestionAmount: number
}

type DetailedImportData = {
  deals: Record<string, unknown>[]
  tags: Record<string, unknown>[]
  contacts: Record<string, unknown>[]
  notes: Record<string, unknown>[]
  ugcs: CompanyInfo[]
}

export interface ImporterContextType {
  // Data
  rawFile: File | null
  importPreview: ImportPreviewDB | null | undefined
  importData: ImportData | null
  products: Product[] | undefined
  isLoading: boolean
  isFetching: boolean
  isLoadingStatus: boolean

  // Modal states
  showHeaderMappingModal: boolean
  setShowHeaderMappingModal: Dispatch<SetStateAction<boolean>>
  showSelectDealProductModal: boolean
  selectedDealProduct: string | null
  setSelectedDealProduct: Dispatch<SetStateAction<string | null>>
  showConfigureExportModal: boolean
  setShowConfigureExportModal: Dispatch<SetStateAction<boolean>>

  // Loading states
  isSaving: boolean
  isUploading: boolean
  isSettingCompanies: boolean
  isSettingTags: boolean
  isSettingDeals: boolean
  isSettingContacts: boolean
  isSettingCompanyNotes: boolean
  isSettingUnmatchedChains: boolean

  // Form states
  hasPendingChanges: boolean
  selectedTab: CSVRowState
  setSelectedTab: Dispatch<SetStateAction<CSVRowState>>
  rowSelection: RowSelectionState
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>
  validateModalData: ValidateModalData | null
  importPreviewStatus: ImportPreviewNoData | null | undefined
  setShowSelectDealProductModal: Dispatch<SetStateAction<boolean>>
  showExportModal: boolean
  setShowExportModal: Dispatch<SetStateAction<boolean>>

  // Computed values
  companySubcategories:
    | {
        category_id: number
        value: number
        label: unknown
        slug: CompanyTypeSlug
      }[]
    | undefined
  mappedColumns: string[] | undefined
  tableData: Record<CSVRowState, CSVRow[]>
  wizardHeaders: Record<string, string>
  otherOtherType:
    | { category_id: number; value: number; slug: CompanyTypeSlug }
    | undefined
  areAllRowsSet: boolean

  // From computedCompanyData
  companyGroups: Map<string, CSVRow[]>
  companiesPrematched: CSVRow[]
  companiesPendingReview: CSVRow[]
  companiesLeftUnmatched: CSVRow[]
  allCompanies: CSVRow[]
  amountCompaniesPrematched: number
  amountCompaniesPendingReview: number
  amountCompaniesLeftUnmatched: number
  importSummaryData: Record<string, unknown>[]
  detailedImportData: DetailedImportData
  importCounts: ImportCounts
  isAllImported: boolean
  uniqueSubcategoryIds: Set<number>

  // Handlers
  updateMap: (original_header: string, mapped_column: string) => void
  updateIsCustomCompany: (row_ids: string[], is_custom_company: boolean) => void
  updateCustomCompanyCategory: (
    row_ids: string[],
    category_id: number,
    subcategory_id: number | null
  ) => void
  updateRowsReady: (row_ids: string[], ready: boolean) => void
  handleManualSearchSelect: (
    door: DoorSearchResult | null,
    chain: ChainSearchResult | null
  ) => void
  handleSetCompanies: () => Promise<void>
  handleSetTags: () => Promise<void>
  handleSetDeals: () => Promise<boolean>
  handleSetContacts: () => Promise<void>
  handleSetCompanyNotes: () => Promise<void>
  handleValidation: (changes: ValidateModalRow[], columnName: string) => void
  handleSelectDealProduct: (product: string) => void
  handleExport: (selectedColumns: string[]) => Promise<void>
  handleFileUpload: (file: File | null) => void
  handleSetUnmatchedChains: () => Promise<void>
  updateMatchedChain: (
    row_id: string,
    matched_chain: ChainSearchResult | null,
    manual_match: boolean
  ) => void
  updateMatchedDoor: (
    row_id: string,
    matched_door: DoorSearchResult | null,
    manual_match: boolean
  ) => void
  handleSave: (data?: ImportData) => Promise<number | undefined>
  setValidateModalData: Dispatch<SetStateAction<ValidateModalData | null>>
  setSelectedRowId: Dispatch<SetStateAction<string | null>>
  selectedRowId: string | null
  shouldShowBulkActions: boolean
  selectedRowIds: string[]
  chainMergerAvailableSubcategories: Record<number, string>
  handleConfigureExport: () => void
  updateMatchedChainFromCompanyHash: (
    company_hash: string,
    matched_chain: ChainSearchResult | null,
    manual_match: boolean
  ) => void
  updateMatchedDoorFromCompanyHash: (
    company_hash: string,
    matched_door: DoorSearchResult | null,
    manual_match: boolean
  ) => void
  updateRowsReadyFromCompanyHash: (hashes: string[], ready: boolean) => void
  updateIsCustomCompanyFromCompanyHash: (
    hashes: string[],
    isCustom: boolean
  ) => void
  updateCustomCompanyCategoryFromCompanyHash: (
    company_hashes: string[],
    category_id: number,
    subcategory_id: number | null
  ) => void

  // Column Filtering
  columnFiltering: ColumnFilter[]
  setSubcategoryFilter: (subcategoryId: number) => void
  clearSubcategoryFilter: () => void
  selectedSubcategoryFilter: unknown

  // Export
  exportColumns: ExportModalColumn[]
  exportConfig: ExportConfig
  setExportConfig: Dispatch<SetStateAction<ExportConfig>>

  // Review
  reviewing: boolean
  setReviewing: Dispatch<SetStateAction<boolean>>
  handleReview: () => void
}

const ImporterContext = createContext<ImporterContextType | null>(null)

export function ImporterProvider({
  importNumber,
  children,
}: {
  importNumber: number | null
  children: ReactNode
}) {
  const navigate = useNavigate()
  const api = apiService()

  const { data: companyCategories } = api.useGetCompanyCategories()
  const { data: salesStages } = api.useGetSalesStages()
  const { data: users } = api.useGetUsers()
  const { data: products } = api.useGetProducts()
  const {
    data: importPreview,
    isPending: isLoading,
    isFetching,
    refetch,
    error: importPreviewError,
  } = useGetImportPreview(importNumber)
  const {
    data: importPreviewStatus,
    isPending: isLoadingStatus,
    refetch: refetchStatus,
    error: statusError,
  } = usePollStatus(importNumber)

  // Handle initialization errors
  const initializationError = importPreviewError || statusError
  if (initializationError) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <div className="text-red-600 text-lg font-medium mb-2">
          Failed to load import data
        </div>
        <div className="text-gray-600 mb-4">
          {initializationError instanceof Error
            ? initializationError.message
            : 'An unexpected error occurred'}
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            refetch()
            refetchStatus()
          }}
        >
          Retry
        </button>
      </div>
    )
  }

  const [showSelectDealProductModal, setShowSelectDealProductModal] =
    useState(false)
  const [selectedDealProduct, setSelectedDealProduct] = useState<string | null>(
    null
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isSettingCompanies, setIsSettingCompanies] = useState(false)
  const [isSettingTags, setIsSettingTags] = useState(false)
  const [isSettingDeals, setIsSettingDeals] = useState(false)
  const [isSettingContacts, setIsSettingContacts] = useState(false)
  const [isSettingCompanyNotes, setIsSettingCompanyNotes] = useState(false)
  const [isSettingUnmatchedChains, setIsSettingUnmatchedChains] =
    useState(false)
  const [importData, setImportData] = useState<ImportData | null>(null)
  const [rawFile, setRawFile] = useState<File | null>(null)
  const [hasPendingChanges, setHasPendingChanges] = useState(false)
  const [selectedTab, setSelectedTab] = useState<CSVRowState>('pending_review')
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [validateModalData, setValidateModalData] =
    useState<ValidateModalData | null>(null)
  const [columnFiltering, setColumnFiltering] = useState<ColumnFilter[]>([])
  const [showHeaderMappingModal, setShowHeaderMappingModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showConfigureExportModal, setShowConfigureExportModal] =
    useState(false)
  const [reviewing, setReviewing] = useState(true)

  const [exportConfig, setExportConfig] = useState<ExportConfig>({
    allowSuggestions: true,
    suggestionAmount: 1,
  })

  /* -------------
  Computed Values
  ------------- */
  const exportColumns = useMemo<ExportModalColumn[]>(() => {
    if (!importData) return []

    function toTitle(key: string) {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
    }

    const baseColumns: ExportModalColumn['elements'] = []
    importData.headers.forEach((h) => {
      baseColumns.push([h.mapped, h.original])
    })

    const suggestionColumns: ExportModalColumn['elements'] = []
    chainSuggestionKeys.forEach((key) => {
      suggestionColumns.push([
        `__suggestion__chain__${key}`,
        `Chain ${toTitle(key)}`,
      ])
    })
    doorSuggestionKeys.forEach((key) => {
      suggestionColumns.push([
        `__suggestion__door__${key}`,
        `Door ${toTitle(key)}`,
      ])
    })

    const generatedColumns: ExportModalColumn['elements'] = []
    generatedColumnKeys.forEach((key) => {
      generatedColumns.push([
        key,
        `${toTitle(key.replace('__generated__', ''))}`,
      ])
    })

    const groups = [
      {
        title: 'Uploaded Data',
        elements: baseColumns,
      },
      {
        title: 'Matched Data',
        elements: generatedColumns,
      },
    ]

    if (exportConfig.allowSuggestions) {
      groups.push({
        title: 'Suggested Data',
        elements: suggestionColumns,
      })
    }

    return groups
  }, [importData, exportConfig])

  const companySubcategories = useMemo(() => {
    return companyCategories?.reduce(
      (acc, c) => {
        c.sub_categories?.forEach((s) => {
          acc.push({
            label: (
              <p className="text-sm">
                {c.name} - {s.name}
              </p>
            ),
            value: s.id,
            category_id: c.id,
            slug: s.slug,
          })
        })
        return acc
      },
      [] as {
        label: any
        value: number
        category_id: number
        slug: CompanyTypeSlug
      }[]
    )
  }, [companyCategories])

  const mappedColumns = useMemo(() => {
    return importData?.headers.map((h) => h.mapped)
  }, [importData])

  const shouldShowBulkActions = useMemo(() => {
    return Object.keys(rowSelection).filter((r) => rowSelection[r]).length > 0
  }, [rowSelection])

  const selectedRowIds = useMemo(() => {
    return Object.keys(rowSelection).filter((r) => rowSelection[r])
  }, [rowSelection])

  const selectedSubcategoryFilter = useMemo(() => {
    return columnFiltering.find(
      (f) => f.id === InternalColumns.EXISTING_CHAIN_PROXY_SUBCATEGORY_ID
    )?.value
  }, [columnFiltering])

  const wizardHeaders = useMemo(() => {
    const map = {
      name: '',
      domain: '',
      address: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      full_address: '',
    }

    importData?.headers.forEach((header) => {
      switch (header.mapped) {
        case 'company_name':
          map.name = header.original
          break
        case 'company_domain':
          map.domain = header.original
          break
        case 'company_address_street':
          map.street = header.original
          break
        case 'company_address_city':
          map.city = header.original
          break
        case 'company_address_state':
          map.state = header.original
          break
        case 'company_address_zip':
          map.zip = header.original
          break
        case 'company_full_address':
          map.full_address = header.original
          break
      }
    })

    return map
  }, [importData?.headers])

  const otherOtherType = useMemo(() => {
    return companySubcategories?.find((subcat) => subcat.slug === 'other-other')
  }, [companySubcategories])

  const computedCompanyData = useMemo(() => {
    const startTime = performance.now()

    if (!importData) {
      return {
        // Phase 1 values
        companyGroups: new Map<string, CSVRow[]>(),
        companiesPrematched: [] as CSVRow[],
        companiesPendingReview: [] as CSVRow[],
        companiesLeftUnmatched: [] as CSVRow[],
        allCompanies: [] as CSVRow[],
        amountCompaniesPrematched: 0,
        amountCompaniesPendingReview: 0,
        amountCompaniesLeftUnmatched: 0,
        // Phase 2 values
        importSummaryData: [] as Record<string, unknown>[],
        detailedImportData: {
          deals: [] as Record<string, unknown>[],
          tags: [] as Record<string, unknown>[],
          contacts: [] as Record<string, unknown>[],
          notes: [] as Record<string, unknown>[],
          ugcs: [] as CompanyInfo[],
        },
        importCounts: {
          ugcs: { total: 0, imported: 0 },
          tags: { total: 0, imported: 0 },
          deals: { total: 0, imported: 0 },
          contacts: { total: 0, imported: 0 },
          notes: { total: 0, imported: 0 },
        },
        isAllImported: true,
        areAllRowsSet: false,
        tableData: { pending_review: [] as CSVRow[], reviewed: [] as CSVRow[] },
        uniqueSubcategoryIds: new Set<number>(),
      }
    }

    // --- Header lookups (once, before loop) ---
    const headers: HeaderLookups = {
      dealHeaders: importData.headers.filter(
        (h) => getColumnOption(h.mapped)?.type === 'deal_data'
      ),
      tagHeader: importData.headers.find(
        (h) => getColumnOption(h.mapped)?.type === 'tag_data'
      ),
      contactHeaders: importData.headers.filter(
        (h) => getColumnOption(h.mapped)?.type === 'contact_data'
      ),
      notesHeader: importData.headers.find(
        (h) => getColumnOption(h.mapped)?.type === 'company_notes'
      ),
      hasDealHeaders: false,
      hasContactHeaders: false,
    }
    headers.hasDealHeaders = headers.dealHeaders.length > 0
    headers.hasContactHeaders = headers.contactHeaders.length > 0

    const companyGroups = new Map<string, CSVRow[]>()
    const allCompanies: CSVRow[] = []
    const companiesPrematched: CSVRow[] = []
    const companiesPendingReview: CSVRow[] = []
    const companiesLeftUnmatched: CSVRow[] = []

    const deals: Record<string, unknown>[] = []
    const tags: Record<string, unknown>[] = []
    const contacts: Record<string, unknown>[] = []
    const notes: Record<string, unknown>[] = []
    const ugcs: CompanyInfo[] = []

    const counts: ImportCounts = {
      ugcs: { total: 0, imported: 0 },
      tags: { total: 0, imported: 0 },
      deals: { total: 0, imported: 0 },
      contacts: { total: 0, imported: 0 },
      notes: { total: 0, imported: 0 },
    }

    // Row-level counters for areAllRowsSet
    let readyRowCount = 0
    let setRowCount = 0

    // Row arrays for tableData
    const reviewedRows: CSVRow[] = []
    const pendingReviewRows: CSVRow[] = []

    // Unique subcategory IDs for chainMergerAvailableSubcategories
    const uniqueSubcategoryIds = new Set<number>()

    const companySummaryMap = new Map<
      string,
      {
        companyInfo: CompanyInfo
        summaryDeals: Record<string, unknown>[]
        summaryTagsSet: Set<string>
        summaryNotesSet: Set<string>
        summaryContacts: Record<string, unknown>[]
      }
    >()

    // === SINGLE LOOP OVER ALL ROWS ===
    for (const row of importData.rows) {
      const hash = row[InternalColumns.COMPANY_HASH]
      const isReady = row[InternalColumns.READY]
      const hasMatch =
        row[InternalColumns.MATCHED_CHAIN] || row[InternalColumns.MATCHED_DOOR]
      const isSet =
        row[InternalColumns.SET_CHAIN_PROXY_ID] !== -1 ||
        row[InternalColumns.IS_CHAIN_MERGED]

      // Count rows for areAllRowsSet
      if (isReady) readyRowCount++
      if (isSet) setRowCount++

      // Collect rows for tableData
      if (isSet) {
        reviewedRows.push(row)
      } else {
        pendingReviewRows.push(row)
      }

      // Collect unique subcategory IDs
      const subcatId = row[InternalColumns.EXISTING_CHAIN_PROXY_SUBCATEGORY_ID]
      if (subcatId !== undefined && subcatId !== null) {
        uniqueSubcategoryIds.add(subcatId)
      }

      // First time seeing this company?
      if (!companyGroups.has(hash)) {
        companyGroups.set(hash, [])
        allCompanies.push(row)

        // Phase 1: Categorize company
        if (isReady) {
          companiesPrematched.push(row)
        } else {
          companiesPendingReview.push(row)
          if (!hasMatch) {
            companiesLeftUnmatched.push(row)
          }
        }

        // Phase 2: Initialize company accumulators
        const companyInfo = buildCompanyInfo(row, hash)
        companySummaryMap.set(hash, {
          companyInfo,
          summaryDeals: [],
          summaryTagsSet: new Set<string>(),
          summaryNotesSet: new Set<string>(),
          summaryContacts: [],
        })

        // UGCs (once per company)
        if (companyInfo.isCustomCompany) {
          ugcs.push(companyInfo)
          counts.ugcs.total++
          if (companyInfo.isImported) counts.ugcs.imported++
        }
      }

      companyGroups.get(hash)!.push(row)

      // Phase 2: Process row data (helper functions update counts inline)
      const summary = companySummaryMap.get(hash)!
      processDealRow(
        row,
        headers,
        summary.companyInfo,
        summary.summaryDeals,
        deals,
        counts,
        isSet
      )
      processTagRow(
        row,
        headers,
        summary.companyInfo,
        summary.summaryTagsSet,
        tags,
        counts,
        isSet
      )
      processContactRow(
        row,
        headers,
        summary.companyInfo,
        summary.summaryContacts,
        contacts,
        counts,
        isSet
      )
      processNoteRow(
        row,
        headers,
        summary.companyInfo,
        summary.summaryNotesSet,
        notes,
        counts,
        isSet
      )
    }

    const importSummaryData = Array.from(companySummaryMap.values()).map(
      ({
        companyInfo,
        summaryDeals,
        summaryTagsSet,
        summaryNotesSet,
        summaryContacts,
      }) => ({
        ...companyInfo,
        deals: summaryDeals,
        tags: Array.from(summaryTagsSet),
        notes: Array.from(summaryNotesSet),
        contacts: summaryContacts,
      })
    )

    const hasData =
      counts.ugcs.total > 0 ||
      counts.tags.total > 0 ||
      counts.deals.total > 0 ||
      counts.contacts.total > 0 ||
      counts.notes.total > 0

    const isAllImported =
      !hasData ||
      (counts.ugcs.imported === counts.ugcs.total &&
        counts.tags.imported === counts.tags.total &&
        counts.deals.imported === counts.deals.total &&
        counts.contacts.imported === counts.contacts.total &&
        counts.notes.imported === counts.notes.total)

    const areAllRowsSet = setRowCount === readyRowCount && setRowCount > 0

    const endTime = performance.now()
    console.log(
      `computedCompanyData took ${(endTime - startTime).toFixed(2)}ms for ${importData.rows.length} rows`
    )

    return {
      companyGroups,
      companiesPrematched,
      companiesPendingReview,
      companiesLeftUnmatched,
      allCompanies,
      amountCompaniesPrematched: companiesPrematched.length,
      amountCompaniesPendingReview: companiesPendingReview.length,
      amountCompaniesLeftUnmatched: companiesLeftUnmatched.length,
      importSummaryData,
      detailedImportData: { deals, tags, contacts, notes, ugcs },
      importCounts: counts,
      isAllImported,
      areAllRowsSet,
      tableData: { pending_review: pendingReviewRows, reviewed: reviewedRows },
      uniqueSubcategoryIds,
    }
  }, [importData])

  const chainMergerAvailableSubcategories = useMemo<
    Record<number, string>
  >(() => {
    const subCats =
      companySubcategories?.filter((s) =>
        computedCompanyData.uniqueSubcategoryIds.has(s.value)
      ) ?? []
    const subCatMap: Record<number, string> = {}
    for (const s of subCats) {
      subCatMap[s.value] = s.label
    }
    return subCatMap
  }, [computedCompanyData.uniqueSubcategoryIds, companySubcategories])

  /* -------------
  Effects
  ------------- */
  useEffect(() => {
    if (importPreview) {
      loadImportPreviewData(importPreview.data)
    }
    return () => {
      setImportData(null)
    }
  }, [importPreview])

  useEffect(() => {
    if (importPreviewStatus?.status === 'idle') {
      refetch()
    }
  }, [importPreviewStatus])

  /* -------------
  Field Handlers
  ------------- */
  function updateMap(original_header: string, mapped_column: string) {
    if (!importData) return
    const data = [...importData.headers]
    const header = data.find((h) => h.original === original_header)
    if (header) {
      header.mapped = mapped_column
    }
    setImportData({ ...importData, headers: data })
    setHasPendingChanges(true)
  }

  function updateMatchedDoor(
    row_id: string,
    matched_door: CSVRow[typeof InternalColumns.MATCHED_DOOR],
    manual_match: boolean
  ) {
    if (!importData) return
    const data = [...importData.rows]
    const row = data.findIndex((r) => r[InternalColumns.ROW_ID] === row_id)
    if (row !== -1) {
      data[row][InternalColumns.MATCHED_DOOR] = matched_door
      data[row][InternalColumns.IS_DOOR_MANUAL_MATCH] = manual_match
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateMatchedDoorFromCompanyHash(
    company_hash: string,
    matched_door: CSVRow[typeof InternalColumns.MATCHED_DOOR],
    manual_match: boolean
  ) {
    if (!importData) return
    const data = [...importData.rows]
    const row = data.findIndex(
      (r) => r[InternalColumns.COMPANY_HASH] === company_hash
    )
    if (row !== -1) {
      data[row][InternalColumns.MATCHED_DOOR] = matched_door
      data[row][InternalColumns.IS_DOOR_MANUAL_MATCH] = manual_match
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateMatchedChain(
    row_id: string,
    matched_chain: CSVRow[typeof InternalColumns.MATCHED_CHAIN],
    manual_match: boolean
  ) {
    if (!importData) return
    const data = [...importData.rows]
    const row = data.findIndex((r) => r[InternalColumns.ROW_ID] === row_id)
    if (row !== -1) {
      data[row][InternalColumns.MATCHED_CHAIN] = matched_chain
      data[row][InternalColumns.IS_CHAIN_MANUAL_MATCH] = manual_match
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateMatchedChainFromCompanyHash(
    company_hash: string,
    matched_chain: CSVRow[typeof InternalColumns.MATCHED_CHAIN],
    manual_match: boolean
  ) {
    if (!importData) return
    const data = [...importData.rows]
    const row = data.findIndex(
      (r) => r[InternalColumns.COMPANY_HASH] === company_hash
    )
    if (row !== -1) {
      data[row][InternalColumns.MATCHED_CHAIN] = matched_chain
      data[row][InternalColumns.IS_CHAIN_MANUAL_MATCH] = manual_match
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateIsCustomCompany(
    row_ids: string[],
    is_custom_company: boolean
  ) {
    if (!importData) return
    const data = [...importData.rows]
    for (const d of data) {
      if (row_ids.includes(d[InternalColumns.ROW_ID])) {
        d[InternalColumns.IS_CUSTOM_COMPANY] = is_custom_company
      }
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateIsCustomCompanyFromCompanyHash(
    company_hashes: string[],
    is_custom_company: boolean
  ) {
    if (!importData) return
    const data = [...importData.rows]
    for (const d of data) {
      if (company_hashes.includes(d[InternalColumns.COMPANY_HASH])) {
        d[InternalColumns.IS_CUSTOM_COMPANY] = is_custom_company
      }
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateCustomCompanyCategory(
    row_ids: string[],
    category_id: number,
    subcategory_id: number | null
  ) {
    if (!importData) return
    const data = [...importData.rows]
    for (const d of data) {
      if (row_ids.includes(d[InternalColumns.ROW_ID])) {
        d[InternalColumns.CUSTOM_COMPANY_CATEGORY_ID] = category_id
        d[InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID] = subcategory_id
        d[InternalColumns.IS_CUSTOM_COMPANY] = true
      }
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateCustomCompanyCategoryFromCompanyHash(
    company_hashes: string[],
    category_id: number,
    subcategory_id: number | null
  ) {
    if (!importData) return
    const data = [...importData.rows]
    for (const d of data) {
      if (company_hashes.includes(d[InternalColumns.COMPANY_HASH])) {
        d[InternalColumns.CUSTOM_COMPANY_CATEGORY_ID] = category_id
        d[InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID] = subcategory_id
        d[InternalColumns.IS_CUSTOM_COMPANY] = true
      }
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateRowsReady(row_ids: string[], ready: boolean) {
    if (!importData) return
    const data = [...importData.rows]
    for (const d of data) {
      if (row_ids.includes(d[InternalColumns.ROW_ID])) {
        d[InternalColumns.READY] = ready
      }
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateRowsReadyFromCompanyHash(
    company_hashes: string[],
    ready: boolean
  ) {
    if (!importData) return
    const data = [...importData.rows]
    for (const d of data) {
      if (company_hashes.includes(d[InternalColumns.COMPANY_HASH])) {
        d[InternalColumns.READY] = ready
      }
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  const handleManualSearchSelect = (
    door: DoorSearchResult | null,
    chain: ChainSearchResult | null
  ) => {
    if (selectedRowId === null || !importData) return

    if (door) {
      updateMatchedDoor(selectedRowId, door, true)
      updateMatchedChain(selectedRowId, chain, true)
    } else if (chain) {
      updateMatchedChain(selectedRowId, chain, true)
    }

    setSelectedRowId(null)
  }

  /* -------------
  Actions
  ------------- */
  async function handleSave(localImportData?: ImportData) {
    setIsSaving(true)

    let dataToSave: ImportData | null = null
    if (localImportData) {
      dataToSave = { ...localImportData }
    } else if (importData) {
      dataToSave = { ...importData }
    }

    if (!dataToSave) {
      return
    }

    const data = toRowsJSON(dataToSave)

    try {
      // Update
      if (importNumber) {
        await updateImportPreview(importNumber, {
          import_data: { data },
        })
      } else {
        // Create
        const rawDataString = await rawFile?.text()
        const fileName = rawFile?.name
        const response = await createImportPreview({
          import_data: {
            data,
            raw_data: rawDataString ?? '',
            name: fileName ?? '',
          },
        })
        navigate(`${PATHS.IMPORT_WIZARD}${response.import_number}`, {
          replace: true,
        })
        return response.import_number
      }
      setHasPendingChanges(false)
      return importNumber
    } catch {
      toast.error('Error saving import preview')
    } finally {
      setIsSaving(false)
    }
  }

  async function handleReview() {
    setReviewing(true)
  }

  async function handleSetCompanies() {
    if (!importData) return
    if (!importNumber) return
    setIsSettingCompanies(true)
    try {
      await handleSave()
      const data = await performSetCompanies(importNumber)
      loadImportPreviewData(data.data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsSettingCompanies(false)
    }
  }

  async function handleSetTags() {
    if (!importData) return
    if (!importNumber) return
    setIsSettingTags(true)
    try {
      const data = await performSetTags(importNumber)
      loadImportPreviewData(data.data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsSettingTags(false)
    }
  }

  async function handleSetDeals(): Promise<boolean> {
    if (!importData) return false
    if (!importNumber) return false

    setIsSettingDeals(true)
    try {
      // Validate Deal Account Owners
      if (importData.headers.some((h) => h.mapped === 'deal_account_owner')) {
        const data = await validateDealAccountOwners(importNumber)
        if (!data.success) {
          setValidateModalData({
            title: 'Invalid Deal Account Owners',
            missing: data.missing,
            found: data.found,
            options:
              users?.map((u) => ({
                label: u.email,
                value: u.email,
              })) ?? [],
            itemName: 'Deal Account Owner',
            columnName: 'deal_account_owner',
          })
          return false
        }
      }

      // Validate Deal Sales Stages
      if (importData.headers.some((h) => h.mapped === 'deal_sales_stage')) {
        const data = await validateDealSalesStages(importNumber)
        if (!data.success) {
          setValidateModalData({
            title: 'Invalid Deal Sales Stages',
            missing: data.missing,
            found: data.found,
            options:
              salesStages?.map((s) => ({
                label: s.stage,
                value: s.stage,
              })) ?? [],
            itemName: 'Deal Sales Stage',
            columnName: 'deal_sales_stage',
          })
          return false
        }
      }

      // Validate Deal Product
      if (!importData.headers.some((h) => h.mapped === 'deal_product')) {
        setShowSelectDealProductModal(true)
        return false
      }

      const data = await performSetDeals(importNumber)
      loadImportPreviewData(data.data)
      return true
    } catch (error) {
      console.error(error)
      toast.error('Failed to set deals. Please try again.')
      return false
    } finally {
      setIsSettingDeals(false)
    }
  }

  async function handleSetContacts() {
    if (!importData) return
    if (!importNumber) return
    setIsSettingContacts(true)
    try {
      const data = await performSetContacts(importNumber)
      loadImportPreviewData(data.data)
    } catch (error) {
      console.error(error)
      toast.error('Failed to set contacts. Please try again.')
    } finally {
      setIsSettingContacts(false)
    }
  }

  async function handleSetCompanyNotes() {
    if (!importData) return
    if (!importNumber) return
    setIsSettingCompanyNotes(true)
    try {
      const data = await performSetCompanyNotes(importNumber)
      loadImportPreviewData(data.data)
    } catch (error) {
      console.error(error)
      toast.error('Failed to set company notes. Please try again.')
    } finally {
      setIsSettingCompanyNotes(false)
    }
  }

  function handleValidation(changes: ValidateModalRow[], columnName: string) {
    if (!importData) return
    const mutData = { ...importData }
    const header = mutData.headers.find((h) => h.mapped === columnName)
    if (!header) return

    for (const change of changes) {
      const changeValue =
        change.mapped === FB_REMOVE_CELL_VALUE ? null : change.mapped

      for (let i = 0; i < mutData.rows.length; i++) {
        const row = mutData.rows[i]
        if (row[header.original] === change.original) {
          mutData.rows[i][header.original] = changeValue
        }
      }
    }

    setImportData(mutData)
    setValidateModalData(null)
  }

  function handleSelectDealProduct(productName: string) {
    if (!importData) return
    const mutData = { ...importData }

    mutData.rows.forEach((row) => {
      row[InternalColumns.DEAL_PRODUCT] = productName
    })

    mutData.headers.push({
      original: InternalColumns.DEAL_PRODUCT,
      mapped: 'deal_product',
      label: 'Deal Product',
    })
    setImportData(mutData)

    setShowSelectDealProductModal(false)
  }

  function handleConfigureExport() {
    setShowConfigureExportModal(false)
    setShowExportModal(true)
  }

  const handleExport = useCallback(
    async (selectedColumns: string[]) => {
      if (!importData) return

      const data = importData.rows

      // Transform rows to include exploded matched fields
      const transformedRows: Record<string, any>[] = []
      for (const row of data) {
        const baseRow: Record<string, any> = {}

        function addColumn(key: string, value: any, row: Record<string, any>) {
          if (selectedColumns.includes(key)) {
            row[key] = value
          }
        }

        // Remove nested objects
        for (const key in row) {
          if (
            [
              InternalColumns.ROW_ID,
              InternalColumns.EXISTING_CHAIN_PROXY_ID,
              InternalColumns.EXISTING_CHAIN_PROXY_TYPE,
              InternalColumns.EXISTING_CHAIN_PROXY_SUBCATEGORY_ID,
              InternalColumns.SET_CHAIN_PROXY_ID,
              InternalColumns.MATCHED_DOOR,
              InternalColumns.MATCHED_CHAIN,
              InternalColumns.SUGGESTED_DOORS,
              InternalColumns.SUGGESTED_CHAINS,
              InternalColumns.IS_CUSTOM_COMPANY,
              InternalColumns.CUSTOM_COMPANY_CATEGORY_ID,
              InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID,
              InternalColumns.READY,
              InternalColumns.IS_CHAIN_MERGED,
              InternalColumns.IS_DOOR_MANUAL_MATCH,
              InternalColumns.IS_CHAIN_MANUAL_MATCH,
              InternalColumns.TAG_SET,
              InternalColumns.DEAL_SET,
              InternalColumns.CONTACT_SET,
              InternalColumns.COMPANY_NOTES_SET,
              InternalColumns.DEAL_PRODUCT,
              InternalColumns.MATCHED_CHAIN_PK,
              InternalColumns.MATCHED_CHAIN_TYPE,
              InternalColumns.COMPANY_HASH,
            ].includes(key as InternalColumns) ||
            key.startsWith('matching__')
          ) {
            continue
          }
          baseRow[key] = row[key]
        }

        // ### SUGGESTIONS COLUMNS ###

        // Add blank columns for completeness
        chainSuggestionKeys.forEach((key) => {
          addColumn(`__suggestion__chain__${key}`, '', baseRow)
        })
        doorSuggestionKeys.forEach((key) => {
          addColumn(`__suggestion__door__${key}`, '', baseRow)
        })

        if (
          // Show only if row is not set - i.e. not imported
          row[InternalColumns.SET_CHAIN_PROXY_ID] === -1 &&
          exportConfig.allowSuggestions
        ) {
          // Add the first suggestions to base row
          chainSuggestionKeys.forEach((key) => {
            addColumn(
              `__suggestion__chain__${key}`,
              row[InternalColumns.SUGGESTED_CHAINS]?.[0]?.[key] ?? '',
              baseRow
            )
          })
          doorSuggestionKeys.forEach((key) => {
            addColumn(
              `__suggestion__door__${key}`,
              row[InternalColumns.SUGGESTED_DOORS]?.[0]?.[key] ?? '',
              baseRow
            )
          })

          // Add extra suggestions as new rows
          const extraSuggestionRows = []
          if (
            row[InternalColumns.SUGGESTED_CHAINS]?.length &&
            row[InternalColumns.SUGGESTED_CHAINS].length > 1
          ) {
            for (const s of row[InternalColumns.SUGGESTED_CHAINS].slice(
              1,
              exportConfig.suggestionAmount
            )) {
              const rowWithSuggestions = { ...baseRow }
              chainSuggestionKeys.forEach((key) => {
                addColumn(
                  `__suggestion__chain__${key}`,
                  s[key],
                  rowWithSuggestions
                )
              })
              extraSuggestionRows.push(rowWithSuggestions)
            }
          }
          if (
            row[InternalColumns.SUGGESTED_DOORS]?.length &&
            row[InternalColumns.SUGGESTED_DOORS].length > 1
          ) {
            for (const s of row[InternalColumns.SUGGESTED_DOORS].slice(
              1,
              exportConfig.suggestionAmount
            )) {
              const rowWithSuggestions = { ...baseRow }
              doorSuggestionKeys.forEach((key) => {
                addColumn(
                  `__suggestion__door__${key}`,
                  s[key],
                  rowWithSuggestions
                )
              })
              extraSuggestionRows.push(rowWithSuggestions)
            }
          }
          if (extraSuggestionRows.length > 0) {
            transformedRows.push(...extraSuggestionRows)
          }
        }

        // ### MATCHED COLUMNS ###
        // Add blank columns
        addColumn('__generated__firstbite_chain_id', '', baseRow)
        addColumn('__generated__firstbite_door_id', '', baseRow)
        addColumn(
          '__generated__chain_name',
          row[InternalColumns.MATCHED_CHAIN]?.name ?? '',
          baseRow
        )
        addColumn(
          '__generated__chain_domain',
          row[InternalColumns.MATCHED_CHAIN]?.domain ?? '',
          baseRow
        )
        addColumn(
          '__generated__chain_size',
          row[InternalColumns.MATCHED_CHAIN]?.count ?? '',
          baseRow
        )

        // Row is set - Add FB ID and Door ID
        if (row[InternalColumns.SET_CHAIN_PROXY_ID] !== -1) {
          // FB ID
          // Get Prefixed Firstbite ID e.g. RE12312
          let firstbiteId =
            (row[InternalColumns.MATCHED_CHAIN]?.type ?? '') +
            (row[InternalColumns.MATCHED_CHAIN]?.item_id ?? '')
          if (row[InternalColumns.IS_CUSTOM_COMPANY]) {
            firstbiteId = 'UG' + row[InternalColumns.SET_CHAIN_PROXY_ID]
          }
          addColumn('__generated__firstbite_chain_id', firstbiteId, baseRow)

          // Get Door ID e.g. 12312
          if (row[InternalColumns.MATCHED_DOOR]) {
            addColumn(
              '__generated__firstbite_door_id',
              row[InternalColumns.MATCHED_DOOR].item_id,
              baseRow
            )
          }
        }

        transformedRows.push(baseRow)
      }

      // Convert to CSV
      const csv = Papa.unparse(transformedRows)

      // Create and trigger download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', 'exported_data.csv')
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    [importData, exportConfig]
  )

  function handleFileUpload(file: File | null) {
    setRawFile(file)
    if (!file) return

    setIsUploading(true)
    Papa.parse<CSVRow>(file, {
      header: true,
      skipEmptyLines: 'greedy',
      delimitersToGuess: [';', ','],
      complete: (results) => {
        const data = results.data.map((row) => {
          return {
            ...Object.fromEntries(
              Object.entries(row).map(([key, value]) => [
                key,
                typeof value === 'string' ? value.slice(0, 200) : undefined,
              ])
            ),
          }
        }) as CSVRow[]
        const importData = {
          rows: data,
          headers:
            results.meta.fields?.map((h) => ({
              original: h,
              mapped: 'ignore',
              label: undefined,
            })) ?? [],
        }
        setImportData(importData)
        setShowHeaderMappingModal(true)
        setIsUploading(false)
      },
      error: (error) => {
        console.error('Error parsing CSV:', error)
        setIsUploading(false)
      },
    })
  }

  async function handleSetUnmatchedChains() {
    if (!importData) return
    if (!importNumber) return
    try {
      setIsSettingUnmatchedChains(true)
      await handleSave()
      await setUnmatchedChains(importNumber)
      refetchStatus()
    } catch (error) {
      console.error(error)
    } finally {
      setIsSettingUnmatchedChains(false)
    }
  }

  function loadImportPreviewData(data: ImportDataRowsFormat) {
    setImportData(fromRowsJSON(data))
  }

  function setSubcategoryFilter(subcategoryId: number) {
    if (Number.isNaN(subcategoryId)) {
      clearSubcategoryFilter()
      return
    }

    setColumnFiltering([
      {
        id: InternalColumns.EXISTING_CHAIN_PROXY_SUBCATEGORY_ID,
        value: subcategoryId,
      },
    ])
  }

  function clearSubcategoryFilter() {
    setColumnFiltering([])
  }

  const value = {
    // Data
    rawFile,
    importPreview,
    importData,
    products,
    isLoading,
    isFetching,
    isLoadingStatus,

    // Modal states
    showHeaderMappingModal,
    setShowHeaderMappingModal,
    showSelectDealProductModal,
    selectedDealProduct,
    setSelectedDealProduct,
    showConfigureExportModal,
    setShowConfigureExportModal,

    // Loading states
    isSaving,
    isUploading,
    isSettingCompanies,
    isSettingTags,
    isSettingDeals,
    isSettingContacts,
    isSettingCompanyNotes,
    isSettingUnmatchedChains,

    // Form states
    hasPendingChanges,
    selectedTab,
    setSelectedTab,
    rowSelection,
    setRowSelection,
    validateModalData,
    importPreviewStatus,
    setShowSelectDealProductModal,
    showExportModal,
    setShowExportModal,

    // Computed values
    companySubcategories,
    mappedColumns,
    wizardHeaders,
    otherOtherType,
    // From computedCompanyData (Phase 1+2 consolidation)
    // Includes: companyGroups, companiesPrematched, companiesPendingReview,
    // companiesLeftUnmatched, allCompanies, amountCompaniesPrematched,
    // amountCompaniesPendingReview, amountCompaniesLeftUnmatched,
    // importSummaryData, detailedImportData, importCounts, isAllImported,
    // areAllRowsSet, tableData
    ...computedCompanyData,

    // Handlers
    updateMap,
    updateIsCustomCompany,
    updateCustomCompanyCategory,
    updateRowsReady,
    handleManualSearchSelect,
    handleSetCompanies,
    handleSetTags,
    handleSetDeals,
    handleSetContacts,
    handleSetCompanyNotes,
    handleValidation,
    handleSelectDealProduct,
    handleExport,
    handleFileUpload,
    handleSetUnmatchedChains,
    updateMatchedChain,
    updateMatchedDoor,
    handleSave,
    setValidateModalData,
    setSelectedRowId,
    selectedRowId,
    shouldShowBulkActions,
    selectedRowIds,
    chainMergerAvailableSubcategories,
    handleConfigureExport,
    updateMatchedChainFromCompanyHash,
    updateMatchedDoorFromCompanyHash,
    updateRowsReadyFromCompanyHash,
    updateIsCustomCompanyFromCompanyHash,
    updateCustomCompanyCategoryFromCompanyHash,

    // Column Filtering
    columnFiltering,
    setSubcategoryFilter,
    clearSubcategoryFilter,
    selectedSubcategoryFilter,

    // Export
    exportColumns,
    exportConfig,
    setExportConfig,

    // Review
    reviewing,
    setReviewing,
    handleReview,
  }

  return (
    <ImporterContext.Provider value={value}>
      {children}
    </ImporterContext.Provider>
  )
}

export function useImporterViewModel(): ImporterContextType {
  const context = useContext(ImporterContext)
  if (!context) {
    throw new Error('useImporterViewModel must be used within ImporterProvider')
  }
  return context
}
// === End Context Setup ===

function toRowsJSON(data: ImportData): ImportDataRowsFormat {
  if (!data.rows.length)
    return {
      headers: [],
      rows: {
        columns: [],
        rows: [],
      },
    }
  const columns = Object.keys(data.rows[0])
  const rows = data.rows.map((obj) => columns.map((key) => obj[key]))
  return {
    headers: data.headers,
    rows: {
      columns,
      rows,
    },
  }
}

function fromRowsJSON(data: ImportDataRowsFormat): ImportData {
  return {
    headers: data.headers,
    rows:
      data.rows?.rows?.map(
        (row) =>
          Object.fromEntries(
            (data.rows?.columns || []).map((key, i) => [key, row[i]])
          ) as CSVRow
      ) || [],
  }
}

export const columnTypeColors: Record<ColumnType, ColumnColors> = {
  // Field that is used to match the company exactly, rendering other matching fields irrelevant
  primary_key: {
    background: '#E3F2FD',
    text: '#1E40AF',
  },
  // Field that is used to match the company
  company_match: {
    background: '#F3E5F5',
    text: '#6B21A8',
  },
  // Regular data
  tag_data: {
    background: '#ffffff',
    text: '#000000',
  },
  deal_data: {
    background: '#ffffff',
    text: '#000000',
  },
  contact_data: {
    background: '#ffffff',
    text: '#000000',
  },
  company_notes: {
    background: '#FFF8E1',
    text: '#F57F17',
  },
  // Ignore column
  ignore: {
    background: 'transparent',
    text: '#4B556340',
  },
  display_only: {
    background: '#ffffff',
    text: '#000000',
  },
}

export const columnOptions = {
  firstbite_id: {
    name: 'First Bite Chain ID',
    type: 'primary_key',
  },
  door_id: {
    name: 'Door ID',
    type: 'primary_key',
  },
  company_name: {
    name: 'Company Name',
    type: 'company_match',
  },
  company_phone: {
    name: 'Company Phone',
    type: 'company_match',
  },
  company_opp_type: {
    name: 'Company Type',
    type: 'company_match',
  },
  company_address_state: {
    name: 'Company State',
    type: 'company_match',
  },
  company_address_city: {
    name: 'Company City',
    type: 'company_match',
  },
  company_address_zip: {
    name: 'Company Zip',
    type: 'company_match',
  },
  company_address_country: {
    name: 'Company Country',
    type: 'company_match',
  },
  company_address_street: {
    name: 'Company Street',
    type: 'company_match',
  },
  company_address_street_2: {
    name: 'Company Street 2',
    type: 'company_match',
  },
  company_domain: {
    name: 'Company Domain',
    type: 'company_match',
  },
  company_full_address: {
    name: 'Company Full Address',
    type: 'company_match',
  },
  tag_name: {
    name: 'Tag Name',
    type: 'tag_data',
  },
  deal_sales_stage: {
    name: 'Deal Sales Stage',
    type: 'deal_data',
  },
  deal_account_owner: {
    name: 'Deal Account Owner Email',
    type: 'deal_data',
  },
  deal_product: {
    name: 'Deal Product',
    type: 'deal_data',
  },
  contact_full_name: {
    name: 'Contact Full Name',
    type: 'contact_data',
  },
  contact_first_name: {
    name: 'Contact First Name',
    type: 'contact_data',
  },
  contact_last_name: {
    name: 'Contact Last Name',
    type: 'contact_data',
  },
  contact_phone: {
    name: 'Contact Phone',
    type: 'contact_data',
  },
  contact_work_email: {
    name: 'Contact Work Email',
    type: 'contact_data',
  },
  contact_personal_email: {
    name: 'Contact Personal Email',
    type: 'contact_data',
  },
  contact_generic_email: {
    name: 'Contact Generic Email',
    type: 'contact_data',
  },
  contact_linkedin_url: {
    name: 'Contact LinkedIn URL',
    type: 'contact_data',
  },
  contact_title: {
    name: 'Contact Title',
    type: 'contact_data',
  },
  contact_label: {
    name: 'Contact Label',
    type: 'contact_data',
  },
  company_notes: {
    name: 'Company Notes',
    type: 'company_notes',
  },
} satisfies Record<string, ColumnOption>

export type ColumnOptionKey = keyof typeof columnOptions

export function getColumnOption(key: string): ColumnOption | undefined {
  return (columnOptions as Record<string, ColumnOption>)[key]
}

const chainSuggestionKeys = [
  'name',
  'domain',
  'count',
  'item_id',
  'type',
  'score',
] as Array<keyof ChainSearchResult>

const doorSuggestionKeys = [
  'item_id',
  'name',
  'domain',
  'latitude',
  'longitude',
  'address',
  'city',
  'state',
  'country',
  'zipcode',
  'chain_id',
  'chain_name',
  'chain_size',
  'type',
  'score',
] as Array<keyof DoorSearchResult>

const generatedColumnKeys = [
  '__generated__firstbite_chain_id',
  '__generated__firstbite_door_id',
  '__generated__chain_name',
  '__generated__chain_domain',
  '__generated__chain_size',
]
